import styles from "./PastOrder.module.css";
import { CircleX } from "lucide-react";
import { useTranslation } from "react-i18next";
import { denormalizeDate, isDateFarEnough } from "../../../utils/calendar";
import { getAdvanceDays } from "../../../utils/products";
import type { CartItem } from "../../../types/cart.types";
import type { Variants } from "motion/react";
import * as motion from "motion/react-client";

type PastOrderErrorTooltipProps = {
    errors: CartItem[];
    onClose: () => void;
    cartDatesISO: string[];
    allProducts: any[];
    packages: any[];
    items: any[];
    activities: any[];
    tooltipVariants: Variants;
};

const PastOrderErrorTooltip = ({
    errors,
    onClose,
    cartDatesISO,
    allProducts,
    packages,
    items,
    activities,
    tooltipVariants,
}: PastOrderErrorTooltipProps) => {
    const { t, i18n } = useTranslation();

    const getLeadTimeDays = (product: any): number | null => {
        const raw = product?.order_in_advance;
        if (raw == null || String(raw).trim() === "") return null;
        return getAdvanceDays(product, 0);
    };

    const orderAvailabilityMessage = (product: any, orderDates: string[]): string => {
        const from = product.order_from ?? "";
        const to = product.order_to ?? "";
        const timeText = from && to ? `${from} - ${to}` : "";

        const validDates = orderDates.filter((d) => {
            const gap = getLeadTimeDays(product);
            return gap == null ? true : isDateFarEnough(gap, d);
        });

        const datesText = validDates.map((d) => denormalizeDate(d)).join(", ");
        return `${t("account.orders.error_restriction")} ${timeText} ${t(
            "account.orders.and"
        )} ${datesText}.`;
    };

    const renderErrorBlock = (title: string, message: string, key: string | number) => (
        <div className={styles.ErrorBlock} key={key}>
            <p className={styles.ErrorBlockTitle}>{title}</p>
            <p className={styles.ErrorBlockText}>{message}</p>
        </div>
    );

    const renderError = (er: CartItem, index: number) => {
        if (er.category === "sales") {
            const product = allProducts.find((p) => p.id === Number(er.id));
            if (!product) return null;
            const title = i18n.language === "en" ? product.name_en || "N/A" : product.name;
            return renderErrorBlock(title, orderAvailabilityMessage(product, cartDatesISO), index);
        }
        if (er.category === "package") {
            const product = packages.find((p) => p.id === Number(er.id));
            if (!product) return null;
            const title = i18n.language === "en" ? product.name_en || "N/A" : product.name;
            return renderErrorBlock(title, orderAvailabilityMessage(product, cartDatesISO), index);
        }
        if (er.category === "storage") {
            const product = items.find((p) => p.id === Number(er.id));
            if (!product) return null;
            const title = i18n.language === "en" ? product.name_en || "N/A" : product.name;
            return renderErrorBlock(title, orderAvailabilityMessage(product, cartDatesISO), index);
        }
        if (er.category === "activities") {
            const product = activities.find((p) => p.id === Number(er.id));
            if (!product) return null;
            const title = i18n.language === "en" ? product.title_en || "N/A" : product.title;
            return renderErrorBlock(title, orderAvailabilityMessage(product, cartDatesISO), index);
        }
        return null;
    };

    return (
        <motion.div
            key="error-tooltip"
            className={styles.ErrorTooltip}
            variants={tooltipVariants}
            initial="hidden"
            animate="show"
            exit="exit"
            layout
        >
            <div className={styles.TooltipClose} onClick={onClose}>
                <CircleX />
            </div>
            <p className={styles.ErrorTitle}>{t("account.orders.error_title")}</p>
            {errors.map(renderError)}
        </motion.div>
    );
};

export default PastOrderErrorTooltip;
